import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Label } from "../../components/ui/label";
import { SearchableSelect } from "../../components/ui/leasingSearchable-select";
import { Input } from "../../components/ui/input";
import { format } from "date-fns";
import {
  Download,
  FileText,
  Building2,
  UserCheck,
  DownloadCloud,
  PenLine,
} from "lucide-react";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";

interface LeasingDocModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleId: string;
  executed_date?: string;
}

interface ApiResponse<T> {
  data: T;
  data_two: T;
  message?: string;
  status?: number;
  schedules?: any[];
  documents: T;
}


export default function LeasingDocModal({ open, onOpenChange, scheduleId }: LeasingDocModalProps) {
  const [lenders, setLenders] = useState<any[]>([]);

  useEffect(() => {
    const fetchLenders = async () => {
      try {
        const response = await apiService.get<ApiResponse<any[]>>("/rental-schedule/get_leasing_lender_name");
        setLenders(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch lenders", error);
        setLenders([]);
      }
    };
    fetchLenders();
  }, []);

  const [selectedLender, setSelectedLender] = useState("");
  const [tsSignedDate, setTsSignedDate] = useState<Date | undefined>(undefined);
  const [signedWithAstrafin, setSignedWithAstrafin] = useState(false);
  const [signedWithLender, setSignedWithLender] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [lenderDocs, setLenderDocs] = useState<any[]>([]);
  const [lenderDocsfilter, setLenderDocsFilter] = useState<any[]>([]);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

  useEffect(() => {
    getDocuments();
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedLender("");
      setTsSignedDate(undefined);
      setLenderDocsFilter([]);
      setSignedWithAstrafin(false);
      setSignedWithLender(false);
      setDownloadingIndex(null);
      setIsDownloading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedLender) {
      setLenderDocsFilter([]);
      return;
    }
    setSignedWithLender(true);
    setSignedWithAstrafin(true);
    clearFilter();
  }, [selectedLender])

  const ensureExecutedDateForDocx = () => {
    if (!tsSignedDate) {
      toast.error("Please select Doc Executed Date to download DOCX");
      return false;
    }
    return true;
  };

  const lenderOptions = (Array.isArray(lenders) ? lenders : [])
    .filter((l: any) => l?.id != null && l?.name != null)
    .map((l: any) => ({
      value: String(l.id),
      label: String(l.name),
    }));

  const getDocuments = async () => {
    try {
      const response = await apiService.get<ApiResponse<any[]>>(`/rental-schedule/get-leasing-lender-docx`);
      setLenderDocs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch leasing documents", error);
      setLenderDocs([]);
    }
  };

  const selectedLenderName = String(
    lenderOptions.find((o) => o.value === selectedLender)?.label ?? ""
  )
    .toLowerCase()
    .replace(/\s+/g, "");
  
  const triggerFileDownload = async (fileName: string, template_name: string) => {
   
    const response = await apiService.post<Blob>(
      "/rental-schedule/download_dynamic_docx_rentalschedule",
      {
        schedule_id: scheduleId,
        templateName: template_name,
        executedDate: tsSignedDate,
        lenderName: selectedLenderName,
      },

      { responseType: "blob" }
    );

    const blob = response as Blob;
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleDownload = async (template_name: string, idx: number,docs_name: string) => {
    if (downloadingIndex !== null) return;

    const isDocx = template_name.endsWith(".docx");
    if (isDocx && !ensureExecutedDateForDocx()) return;

    setDownloadingIndex(idx);

    try {
      if (isDocx) {
        await triggerFileDownload(`${scheduleId}-${docs_name}.docx`, template_name);
      } else {
        await downloadPdf(`${scheduleId}-${docs_name}.pdf`,template_name);
      }
      toast.success("Downloaded successfully");
    } catch (error) {
      toast.error("Failed to download document");
    } finally {
      setDownloadingIndex(null);
    }
  };

  const triggerFileDownload_zip = async (fileName: string) => {

    const doc_list = lenderDocsfilter.map((doc: any) => doc.docx_name);

    const blob: any = await apiService.post(
      "/rental-schedule/download_dynamic_docx_rentalschedule_zip",
      {
        schedule_id: scheduleId,
        templateNameList: doc_list,
        executedDate: tsSignedDate,
        lenderName: selectedLenderName,
      },
      { responseType: "blob" }
    );

    if (!blob || blob.size === 0) {
      throw new Error("File download failed or empty response");
    }
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  const handleDownload_zip = async () => {
    if (isDownloading) return;
    if (!ensureExecutedDateForDocx()) return;

    setIsDownloading(true);

    try {
      await triggerFileDownload_zip(`Rental-Schedule-${scheduleId}.zip`);
      toast.success("Downloaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to download document");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const downloadPdf = async (name: string, template_name: string) => {
    try {
      const blob = await apiService.post<Blob>(
        "/rental-schedule/download_pdf",
        { templateName: template_name,
          lenderName: selectedLenderName
         },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = name;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const customerFilter = () => {
    setLenderDocsFilter([]);
    const filterOne = lenderDocs.filter((data: any) => data.signed_with_customer == true && data.lender_id === Number(selectedLender));
    setLenderDocsFilter(filterOne);
  }

  const lenderFilter = () => {
    setLenderDocsFilter([]);
    const filterTwo = lenderDocs.filter((data: any) => data.signed_with_lender == true && data.lender_id === Number(selectedLender));
    setLenderDocsFilter(filterTwo);
  }

  const clearFilter = () => {
    setLenderDocsFilter([]);
    const filterThree = lenderDocs.filter((data: any) => data.lender_id === Number(selectedLender));
    setLenderDocsFilter(filterThree);
  }


  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl overflow-visible">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Leasing Documents
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 overflow-visible">
          {/* Lender & Date Row */}
          <div className="grid grid-cols-2 gap-3 overflow-visible">
            <div className="space-y-1.5 overflow-visible">
              <Label className="text-xs font-medium text-muted-foreground">Lender Name</Label>
              <SearchableSelect
                options={lenderOptions}
                value={selectedLender}
                onValueChange={setSelectedLender}
                placeholder="Select lender..."
                searchPlaceholder="Search lender..."
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Doc Executed Date <span className="text-destructive">*</span>
                <span className="ml-1 font-normal">(required for DOCX)</span>
              </Label>
              <Input
                type="date"
                value={tsSignedDate ? format(tsSignedDate, "yyyy-MM-dd") : ""}
                onChange={(e) => setTsSignedDate(e.target.value ? new Date(e.target.value) : undefined)}
                className="h-9"
              />
            </div>
          </div>

          {/* Signed With Tabs */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Signed With</Label>
            <Tabs
              value={signedWithAstrafin && signedWithLender ? "both" : signedWithAstrafin ? "astrafin" : signedWithLender ? "lender" : "none"}
              onValueChange={(val) => {
                setSignedWithAstrafin(val === "astrafin" || val === "both");
                setSignedWithLender(val === "lender" || val === "both");
              }}
            >
              <TabsList className="w-full">
                <TabsTrigger value="astrafin" className="flex-1 gap-1.5 text-xs" onClick={customerFilter}>
                  <PenLine className="h-3.5 w-3.5" />
                  Customer
                </TabsTrigger>
                <TabsTrigger value="lender" className="flex-1 gap-1.5 text-xs" onClick={lenderFilter}>
                  <Building2 className="h-3.5 w-3.5" />
                  Lender
                </TabsTrigger>
                <TabsTrigger value="both" className="flex-1 gap-1.5 text-xs" onClick={clearFilter}>
                  <UserCheck className="h-3.5 w-3.5" />
                  Both
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Document List */}
          <div className="border rounded-lg divide-y max-h-[320px] overflow-y-auto">
            {lenderDocsfilter.map((filteredList, idx) => (
              <div
                key={filteredList.id}
                className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{filteredList.DocsName}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleDownload(filteredList.docx_name, idx,filteredList.DocsName)}
                  disabled={downloadingIndex !== null}
                >
                  {downloadingIndex === idx ? (
                    <span className="flex items-center">
                      <svg className="animate-spin h-3.5 w-3.5 mr-1" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Downloading...
                    </span>
                  ) : (
                    <>
                      <Download className="h-3.5 w-3.5 mr-1" />
                      Download
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* Download All */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleDownload_zip}
            disabled={isDownloading || lenderDocsfilter.length === 0}
          >
            {isDownloading ? (
              <span className="flex items-center justify-center w-full">
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Downloading...
              </span>
            ) : (
              <>
                <DownloadCloud className="h-4 w-4 mr-2" />
                Download All Documents
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
